import React, { memo, useRef } from 'react'

import { Button, Icon, ImagesHistory } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import useBreakpoint from 'Utils/useBreakpoint'

import HistoryMenu from './Components/HistoryMenu/HistoryMenu'

// Floats over the canvas viewport's top-right corner. Overlaying arbitrary artwork is
// why the wrapper carries a solid fill and the trigger keeps its border - the glyph has
// to stay legible on a dark image, and the disabled state must not let the image show
// through its 50% opacity.
function HistoryControl({
  versions,
  versionCount = 0,
  currentId = null,
  isOpen,
  isSending,
  onToggle,
  onSelectVersion,
  onClose,
}) {
  const t = useTranslation()
  const triggerRef = useRef(null)
  // Below `sm` the labelled trigger eats too much of the image, so it drops to an
  // icon-only button and keeps its name through aria-label. The Edit tab only
  // mounts once an image is loaded - never during SSR - so branching on the
  // breakpoint here cannot desync hydration.
  const isMobile = useBreakpoint() === 'xs'
  const label = t('imageAssistantPanel.editTab.toolbar.history')
  const icon = <Icon svgComponent={ImagesHistory} size={isMobile ? 'tiny' : iconSize} color="dark" notSpaced />

  const showBadge = versionCount > 0
  const badge = showBadge
    ? (
      <span
        className="image-assistant-edit-tab__history-badge cgt-caption-semibold cgt-caption-semibold--dark-bg"
        aria-hidden="true"
      >
        {versionCount}
      </span>
    )
    : null

  return (
    <div className="image-assistant-edit-tab__history" ref={triggerRef}>
      <Button
        type="secondary"
        size="small"
        variant={isMobile ? 'only-icon' : 'with-icon'}
        actionType="button"
        customClass="image-assistant-edit-tab__history-button"
        icon={icon}
        trailIcon={badge}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={showBadge ? `${label} (${versionCount})` : label}
        disabled={versions.length === 0 || isSending}
        onClick={onToggle}
      >
        {isMobile ? null : label}
      </Button>
      {isOpen && (
        <HistoryMenu
          versions={versions}
          currentId={currentId}
          triggerRef={triggerRef}
          onSelect={onSelectVersion}
          onClose={onClose}
        />
      )}
    </div>
  )
}

HistoryControl.propTypes = {
  versions: arrayOf(shape({
    id: number.isRequired,
    url: string.isRequired,
    label: string.isRequired,
    kind: string,
    prompt: string,
  })).isRequired,
  versionCount: number,
  currentId: number,
  isOpen: bool.isRequired,
  isSending: bool.isRequired,
  onToggle: func.isRequired,
  onSelectVersion: func.isRequired,
  onClose: func.isRequired,
}

export default memo(HistoryControl)
